import { useRef, forwardRef } from 'react';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { IcoScheduleLine } from '@myrealtrip/web-icons';
import {
  MenuListContent,
  MenuListRoot,
  MenuListTrigger,
  Text,
  usePreventScroll,
} from '@myrealtrip/web-ui';

import Show from '~components/Show';
import {
  DEPARTURE_TIME_OPTION_LIST_HEIGHT,
  DEPARTURE_TIME_OPTION_LIST_WIDTH,
  DEPARTURE_TIME_OPTION_CONTAINER_PADDING,
} from '~constants/common';

import TimeOptionList from './TimeOptionList';

interface Props {
  isOutboundSelectorOpen: boolean;
  outboundTime?: string | null;
  onClickOutboundTime: () => void;
  onSelectOutboundTime: (time: string) => () => void;
}

const SingleTimeSelector = forwardRef<HTMLUListElement, Props>(
  (
    { isOutboundSelectorOpen, outboundTime, onClickOutboundTime, onSelectOutboundTime }: Props,
    timeOptionRef,
  ) => {
    const { colors } = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);

    usePreventScroll({ isDisabled: !isOutboundSelectorOpen });

    return (
      <Container ref={containerRef}>
        <MenuListRoot
          isOpen={isOutboundSelectorOpen}
          positionTop={
            -(containerRef.current?.clientHeight || 0) +
            DEPARTURE_TIME_OPTION_CONTAINER_PADDING -
            DEPARTURE_TIME_OPTION_LIST_HEIGHT
          }
        >
          <TimeOption onClick={onClickOutboundTime}>
            <LabelWrapper>
              <IcoScheduleLine size={16} color={colors.gray.s700} />
              <Text typography="paragraphNormal15" color={colors.gray.s700}>
                가는날 시간
              </Text>
            </LabelWrapper>
            <StyledMenuListTrigger>
              <SelectedTime typography="paragraphNormal16" as="p">
                {outboundTime} 이후
              </SelectedTime>
            </StyledMenuListTrigger>
          </TimeOption>
          <MenuListContent>
            <Show when={isOutboundSelectorOpen}>
              <TimeOptionList
                selectedTime={outboundTime}
                onSelectTime={onSelectOutboundTime}
                ref={timeOptionRef}
              />
            </Show>
          </MenuListContent>
        </MenuListRoot>
      </Container>
    );
  },
);

SingleTimeSelector.displayName = 'SingleTimeSelector';

export default SingleTimeSelector;

const Container = styled.div`
  width: 100%;
`;

const TimeOption = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.colors.gray.s60};
  border-radius: 16px;
  padding: 13px 16px;
  cursor: pointer;
`;

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding-left: 4px;
`;

const StyledMenuListTrigger = styled(MenuListTrigger)`
  flex-basis: ${DEPARTURE_TIME_OPTION_LIST_WIDTH}px;
  text-align: right;
`;

const SelectedTime = styled(Text)`
  color: ${({ theme }) => theme.colors.gray.s1000};
  padding-right: 4px;
`;
